import axios, { AxiosInstance } from 'axios';

// API Base URL - determine based on how the frontend is served
// When served from nginx (Docker, port 3000), use /api baseURL - nginx proxies and strips /api prefix
// When running React dev server (typically port 3001), connect directly to backend at localhost:8000
// Cache bust: v3
const API_BASE_URL = process.env.REACT_APP_API_URL ||
  (window.location.port === '3001' ? 'http://localhost:8000' : '/api');

// Debug log for API base URL (remove after testing)
console.log('[API] Base URL:', API_BASE_URL || '(empty - using relative paths)', 'Port:', window.location.port);

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and language preference
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const refreshToken = localStorage.getItem('refresh_token');
    if (refreshToken) {
      config.headers['X-Refresh-Token'] = refreshToken;
    }

    // Add user's language preference from i18next
    const i18nextLng = localStorage.getItem('i18nextLng');
    if (i18nextLng) {
      config.headers['Accept-Language'] = i18nextLng;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 and not already retried, try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });

          const { access_token, refresh_token: newRefreshToken } = response.data;
          localStorage.setItem('access_token', access_token);
          localStorage.setItem('refresh_token', newRefreshToken);

          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, logout user
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;

// API Service Types
export interface User {
  id: number | string;
  username: string;
  email: string;
  created_at?: string;
}

export interface LoginCredentials {
  username: string;
  email: string;
  password: string;
}

export interface RegisterData {
  username: string;
  email: string;
  password: string;
}

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
}

export interface Document {
  id: number | string;
  filename: string;
  s3_key?: string;
  content_type?: string;
  created_at?: string;
}

export interface ProcessingJob {
  id: number | string;
  document_id: number | string;
  status: 'pending' | 'running' | 'finished' | 'failed';
  result?: string;
  created_at?: string;
  started_at?: string;
  finished_at?: string;
}

// Auth API
export const authAPI = {
  register: async (data: RegisterData): Promise<User> => {
    const response = await apiClient.post<User>('/auth/register', data);
    return response.data;
  },

  login: async (credentials: LoginCredentials): Promise<TokenResponse> => {
    const response = await apiClient.post<TokenResponse>('/auth/login', credentials);
    return response.data;
  },

  refresh: async (refreshToken: string): Promise<TokenResponse> => {
    const response = await apiClient.post<TokenResponse>('/auth/refresh', {
      refresh_token: refreshToken,
    });
    return response.data;
  },

  logout: async (refreshToken: string): Promise<void> => {
    await apiClient.post('/auth/logout', { refresh_token: refreshToken });
  },

  verifyEmail: async (email: string, code: string): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>('/auth/verify-email', {
      email,
      code,
    });
    return response.data;
  },

  resendVerificationEmail: async (email: string): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>('/auth/resend-verification-email', {
      email,
    });
    return response.data;
  },

  resetPassword: async (email: string, newPassword: string): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>('/auth/reset-password', {
      email,
      new_password: newPassword,
    });
    return response.data;
  },
};

// Documents API
export const documentsAPI = {
  list: async (): Promise<Document[]> => {
    const response = await apiClient.get<Document[]>('/documents/');
    return response.data;
  },

  upload: async (files: FileList | File[]): Promise<ProcessingJob[]> => {
    const formData = new FormData();
    Array.from(files).forEach((file) => {
      formData.append('file', file);
    });

    const response = await apiClient.post<ProcessingJob[]>('/documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  download: async (documentId: number | string): Promise<Blob> => {
    const response = await apiClient.get(`/documents/${documentId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  delete: async (documentId: number | string): Promise<void> => {
    await apiClient.delete(`/documents/${documentId}`);
  },

  // AI Actions
  summarize: async (documentId: number | string, sync: boolean = true): Promise<ProcessingJob> => {
    const response = await apiClient.post<ProcessingJob>(
      `/documents/${documentId}/actions/summarize?sync=${sync}`
    );
    return response.data;
  },

  extractKeyPoints: async (documentId: number | string, sync: boolean = true): Promise<ProcessingJob> => {
    const response = await apiClient.post<ProcessingJob>(
      `/documents/${documentId}/actions/extract_keypoints?sync=${sync}`
    );
    return response.data;
  },

  extractKeywords: async (documentId: number | string, sync: boolean = true): Promise<ProcessingJob> => {
    const response = await apiClient.post<ProcessingJob>(
      `/documents/${documentId}/actions/extract_keywords?sync=${sync}`
    );
    return response.data;
  },

  findSimilarDocuments: async (documentId: number | string, sync: boolean = true): Promise<ProcessingJob> => {
    const response = await apiClient.post<ProcessingJob>(
      `/documents/${documentId}/actions/find_similar?sync=${sync}`
    );
    return response.data;
  },

  answerQuestions: async (
    documentId: number | string,
    question: string,
    sync: boolean = true
  ): Promise<ProcessingJob> => {
    const response = await apiClient.post<ProcessingJob>(
      `/documents/${documentId}/actions/answer_questions?sync=${sync}`,
      { question }
    );
    return response.data;
  },

  compareDocuments: async (documentId: number | string, sync: boolean = true): Promise<ProcessingJob> => {
    const response = await apiClient.post<ProcessingJob>(
      `/documents/${documentId}/actions/comparison_analysis?sync=${sync}`
    );
    return response.data;
  },

  findDifferences: async (documentId: number | string, sync: boolean = true): Promise<ProcessingJob> => {
    const response = await apiClient.post<ProcessingJob>(
      `/documents/${documentId}/actions/difference_seeker?sync=${sync}`
    );
    return response.data;
  },

  similaritySearch: async (documentId: number | string, sync: boolean = true): Promise<ProcessingJob> => {
    const response = await apiClient.post<ProcessingJob>(
      `/documents/${documentId}/actions/similarity_search?sync=${sync}`
    );
    return response.data;
  },

  clearSessionFiles: async (): Promise<void> => {
    await apiClient.delete('/documents/session/files');
  },

  generalQuery: async (question: string, sync: boolean = true): Promise<ProcessingJob> => {
    const response = await apiClient.post<ProcessingJob>(
      `/documents/general_query?sync=${sync}`,
      { question }
    );
    return response.data;
  },
};

// Jobs API
export const jobsAPI = {
  getStatus: async (jobId: number | string): Promise<ProcessingJob> => {
    const response = await apiClient.get<ProcessingJob>(`/documents/jobs/${jobId}`);
    return response.data;
  },
};

// Export API
export interface ExportRequest {
  content: string;
  title: string;
  format: 'docx' | 'pdf' | 'txt';
}

export const exportAPI = {
  exportDocument: async (request: ExportRequest): Promise<Blob> => {
    const response = await apiClient.post('/export/document', request, {
      responseType: 'blob',
    });
    return response.data;
  },
};
